import React, { useCallback, useRef, useState } from 'react'
import { LabraToolTipV2 } from '@labrav/react-components'
import { createSafeResizeObserver } from '../../utils/safeResizeObserver'
import { useStyles } from './EllipsisText.styles'

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right'

interface EllipsisTextProps {
  /** The text to render on a single line, clipped with a width-based ellipsis. */
  text: string
  /** Caller styles (font, color, flex sizing, etc.). */
  className?: string
  /** Where the hover tooltip appears when the text is clipped. */
  tooltipPlacement?: TooltipPlacement
}

/**
 * Renders text on a single line with a CSS `text-overflow: ellipsis` (so it
 * clips only when it actually reaches its width) and reveals the full text in a
 * `LabraToolTipV2` hover tooltip ONLY when it's truncated.
 *
 * The measured clip element is the OUTER div (stable — it never remounts when
 * the tooltip toggles, which would otherwise thrash the ResizeObserver and loop
 * to a crash). LabraToolTipV2 wraps only the text; its internal wrapper div is
 * forced inline (see styles) so the parent's ellipsis still applies. Truncation
 * is detected by measuring `scrollWidth > clientWidth`, re-checked on resize.
 */
export const EllipsisText: React.FC<EllipsisTextProps> = ({
  text,
  className,
  tooltipPlacement = 'top',
}) => {
  const { classes, cx } = useStyles()
  const [isTruncated, setIsTruncated] = useState(false)
  const observerRef = useRef<ResizeObserver | null>(null)

  const measuredRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect()
    if (!node) return
    const measure = () => setIsTruncated(node.scrollWidth > node.clientWidth)
    measure()
    observerRef.current = createSafeResizeObserver(measure)
    observerRef.current.observe(node)
  }, [])

  return (
    <div ref={measuredRef} className={cx(classes.clip, className)}>
      <LabraToolTipV2
        tooltipPlacement={tooltipPlacement}
        description={isTruncated ? text : ''}
      >
        {text}
      </LabraToolTipV2>
    </div>
  )
}
